/**
 * Sub-components for AdminDonations.jsx.
 *
 * ManualDonationForm: cash/cheque manual entry form.
 * DonationsTable: read-only table with Issue-80G / PDF actions.
 */
import React from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, HandHeart } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export function ManualDonationForm({ form, set, busy, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="vs-card p-5 space-y-3 lg:col-span-1" data-testid="manual-donation-form">
      <div className="eyebrow flex items-center gap-1.5"><HandHeart size={12} /> Record cash / cheque donation</div>
      <div><Label>Donor name *</Label><Input data-testid="md-donor-name" required value={form.donor_name} onChange={(e) => set("donor_name", e.target.value)} className="bg-white" placeholder="Walk-in donor / cheque payer" /></div>
      <div><Label>Donor id <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(if existing)</span></Label><Input data-testid="md-donor-id" value={form.donor_id} onChange={(e) => set("donor_id", e.target.value)} className="bg-white font-mono" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Amount *</Label><Input data-testid="md-amount" required type="number" min="1" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-white font-mono" /></div>
        <div>
          <Label>Method</Label>
          <Select value={form.method} onValueChange={(v) => set("method", v)}>
            <SelectTrigger data-testid="md-method" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="bank">Bank transfer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Category</Label>
        <Select value={form.category} onValueChange={(v) => set("category", v)}>
          <SelectTrigger data-testid="md-category" className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="birthday">Birthday</SelectItem>
            <SelectItem value="festival">Festival</SelectItem>
            <SelectItem value="medical">Medical</SelectItem>
            <SelectItem value="boys_hostel">Boys Hostel</SelectItem>
            <SelectItem value="girls_hostel">Girls Hostel</SelectItem>
            <SelectItem value="other_contribution">Other Contribution</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div><Label>Note</Label><Textarea data-testid="md-note" value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className="bg-white" /></div>
      <Button type="submit" disabled={busy} data-testid="md-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
        <Plus size={12} className="mr-1.5" /> {busy ? "Saving…" : "Record donation"}
      </Button>
    </form>
  );
}

function DonationRow({ row, onIssued }) {
  const issue80g = async () => {
    try {
      const res = await api.post(`/donations/${row.id}/issue-80g`, { donation_for: row.category || "Seva activities" });
      toast.success(`Receipt ${res.data.receipt_no_80g} issued`);
      onIssued();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };
  const sourceBadgeClass = row.manual_entry
    ? "border-[var(--vs-warning)]/40 text-[var(--vs-warning)]"
    : "border-[var(--vs-secondary)]/40";
  return (
    <tr className="border-t border-[var(--vs-border)]" data-testid={`adm-don-row-${row.id}`}>
      <td className="px-4 py-3 font-mono text-xs">{(row.created_at || "").slice(0, 10)}</td>
      <td className="px-4 py-3">{row.donor_name || row.donor_id?.slice(0, 8) || "—"}</td>
      <td className="px-4 py-3 capitalize">{row.category}</td>
      <td className="px-4 py-3 font-mono text-[10px]">{row.receipt_no_80g || <span className="text-[var(--vs-text-secondary)]">—</span>}</td>
      <td className="px-4 py-3 text-right font-mono">{fmt(row.amount)}</td>
      <td className="px-4 py-3 text-[10px]"><Badge variant="outline">{row.method?.toUpperCase()}</Badge></td>
      <td className="px-4 py-3 text-[10px]"><Badge variant="outline" className={sourceBadgeClass}>{row.manual_entry ? "MANUAL" : "ONLINE"}</Badge></td>
      <td className="px-4 py-3 text-right">
        {row.receipt_no_80g ? (
          <Button asChild size="sm" variant="outline" data-testid={`adm-don-dl-${row.id}`}>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/donations/${row.id}/80g-receipt.pdf`} target="_blank" rel="noopener noreferrer">PDF</a>
          </Button>
        ) : (
          <Button size="sm" variant="outline" data-testid={`adm-don-issue-${row.id}`} onClick={issue80g}>Issue 80G</Button>
        )}
      </td>
    </tr>
  );
}

export function DonationsTable({ rows, onIssued }) {
  const total = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
  const manual = rows.filter((r) => r.manual_entry).length;
  return (
    <div className="lg:col-span-2 vs-card overflow-x-auto self-start">
      <div className="px-4 py-3 flex items-center justify-between border-b border-[var(--vs-border)]">
        <div className="eyebrow">All donations</div>
        <div className="text-xs text-[var(--vs-text-secondary)]">
          Total: <span className="font-mono font-semibold text-[var(--vs-success)]" data-testid="donations-total">{fmt(total)}</span> · {rows.length} entries · {manual} manual
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[var(--vs-bg-subtle)]">
          <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Donor</th>
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Receipt</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3">Method</th>
            <th className="px-4 py-3">Source</th>
            <th className="px-4 py-3 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 200).map((row) => (
            <DonationRow key={row.id} row={row} onIssued={onIssued} />
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No donations yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
